<template>
  <div class="dns-lookup-app" data-testid="dns-lookup-app">
    <div class="dns-lookup-container">
      <h2 v-if="config.show_title" class="app-title" data-testid="dns-lookup-title">{{ config.title }}</h2>
      
      <a-card class="lookup-card" data-testid="dns-lookup-card">
        <domain-input
          v-model="domain"
          :loading="isLoading"
          @submit="handleLookup"
        />
        
        <a-divider />
        
        <record-type-selector v-model="selectedRecordTypes" />
      </a-card>
      
      <a-alert
        v-if="error"
        type="error"
        :message="error"
        closable
        @close="error = null"
        style="margin-top: 16px;"
        data-testid="dns-lookup-error"
      />
      
      <div ref="resultsSection">
        <records-display :results="results" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, nextTick } from 'vue'
import { message } from 'ant-design-vue'
import DomainInput from './components/DomainInput.vue'
import RecordTypeSelector from './components/RecordTypeSelector.vue'
import RecordsDisplay from './components/RecordsDisplay.vue'
import { useDnsLookup } from './composables/useDnsLookup'
import type { DnsLookupConfig } from './types'

interface Props {
  mountElementId: string
}

const props = defineProps<Props>()

const domain = ref('')
const selectedRecordTypes = ref<string[]>(['A', 'AAAA', 'MX', 'TXT'])
const config = ref<DnsLookupConfig>({
  instance_id: '',
  title: 'DNS Lookup Tool',
  show_title: true,
  default_records: ['A', 'AAAA', 'MX', 'TXT'],
  show_propagation: false
})

const { isLoading, results, error, lookup } = useDnsLookup()

const resultsSection = ref<HTMLElement | null>(null)

watch(results, (newResults) => {
  if (newResults && Object.keys(newResults).length > 0) {
    nextTick(() => {
      resultsSection.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
  }
})

onMounted(() => {
  // Get configuration from data attribute
  const mountElement = document.getElementById(props.mountElementId)
  if (mountElement) {
    const configAttr = mountElement.getAttribute('data-config')
    if (configAttr) {
      try {
        const parsedConfig = JSON.parse(configAttr)
        config.value = { ...config.value, ...parsedConfig }
        
        // Set default record types from config
        if (parsedConfig.default_records && parsedConfig.default_records.length > 0) {
          selectedRecordTypes.value = parsedConfig.default_records
        }
      } catch (err) {
        console.error('Failed to parse DNS lookup config:', err)
      }
    }
  }
})

const handleLookup = async (lookupDomain: string) => {
  if (!lookupDomain || selectedRecordTypes.value.length === 0) {
    message.warning('Please enter a domain and select at least one record type')
    return
  }
  
  await lookup(lookupDomain, selectedRecordTypes.value)
}
</script>

<style scoped lang="scss">
.dns-lookup-app {
  padding: 24px 0;
  
  .dns-lookup-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 24px;
    
    .app-title {
      text-align: center;
      margin-bottom: 32px;
      font-size: 32px;
      font-weight: 700;
      color: #262626;
    }
    
    .lookup-card {
      margin-bottom: 24px;
      padding: 24px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
      
      :deep(.ant-card-body) {
        padding: 24px;
      }
      
      :deep(.ant-divider) {
        margin: 24px 0;
        border-color: #e8e8e8;
      }
    }
  }
}
</style>
